import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Button, notification, Row, Select, Typography, Icon } from 'antd'
import moment from 'moment'

import CreateCultivars from './CreateCultivars'
import ListCultivars from './ListCultivars'
import SumCultivars from './SumCultivars'
import { api, request } from '../../../util/handlerRequestUtil'
import { getMessage } from '../../../util/IntlMessages'
import checkIsSameMonthYear from '../../../util/checkIsSameMonthYear'
import { sumCultivarsProperties } from '../../../util/sumCultivarsProperties'
import { sanitizeCultivarItem } from '../../../util/sanitizeCultivarItem'

import './Cultivars.css'

const Cultivars = ({ currentUser, customerId, districts }) => {
  const [materials, setMaterials] = useState([])
  const [demandTypes, setDemandTypes] = useState([])
  const [cultivarsTypes, setCultivarsTypes] = useState([])
  const [cultivarsTypesCheck, setCultivarsTypesCheck] = useState()
  const [selectedDistrict, setSelectedDistrict] = useState()
  const cultivarsInitialState = {
    totalArea: 0,
    checkArea: 0,
    i2xArea: 0,
    i2xOpportunity: 0,
    organizationArea: 0,
    organizationOpportunity: 0,
    cultivarsItems: []
  }
  const [cultivars, setCultivars] = useState(cultivarsInitialState)

  useEffect(() => {
    fetchMaterials()
    fetchDemandTypes()
    fetchCultivarsTypes()
    fetchCheckId()
    if (districts.length === 1) {
      fetchCultivars(customerId, selectedDistrict)
    }
  }, [])

  useEffect(() => {
    const { cultivarsItems } = cultivars
    const totalArea = sumCultivarsProperties(cultivarsItems)
    const checkArea = sumCultivarsProperties(
      cultivarsItems,
      (item) => item.cultivarsDemandTypeId === cultivarsTypesCheck
    )

    let i2xAreaArray = cultivarsItems.filter((cultivar) => {
      if (cultivar.materials && cultivar.materials.tecnologia.name === 'i2x') {
        return cultivar
      }
    })
    const i2xArea = i2xAreaArray.reduce((acc, item) => acc + Number(item.area), 0)
    const organizationArea = 0

    setCultivars({
      ...cultivars,
      totalArea,
      checkArea,
      i2xArea,
      organizationArea,
      i2xOpportunity: totalArea - i2xArea,
      organizationOpportunity: totalArea - organizationArea
    })
  }, [cultivars.cultivarsItems, currentUser])

  async function fetchMaterials() {
    const materialsList = await request(api.getMaterialsWithTecnologia())
    setMaterials(materialsList)
  }

  async function fetchDemandTypes() {
    const demandTypesList = await request(api.getCultivarsDemandTypesDropdown({ todos: false, descricao: true }))
    setDemandTypes(demandTypesList)
  }

  async function fetchCultivarsTypes() {
    const cultivarsTypesList = await request(api.getCultivarsTypesDropdown({ todos: false, descricao: true }))
    setCultivarsTypes(cultivarsTypesList)
  }

  async function fetchCheckId() {
    const [cultivarsTypesCheck] = await request(api.getCultivarsDemandTypes({ name: 'CHECK' }))
    setCultivarsTypesCheck(cultivarsTypesCheck.id)
  }

  async function fetchCultivars(customerId, districtId) {
    const response = await request(api.getCultivarsByCustomer(customerId, { districtId }))
    if (response) {
      setCultivars(response)
    }
  }

  function attachMaterialInCultivarsItem(item) {
    const [material] = materials.filter((mat) => mat.id === item.materialsId)
    return { ...item, materials: material }
  }

  function pushCultivarsItem(item) {
    const cultivarsItem = attachMaterialInCultivarsItem(item)
    let cultivarsItems = []
    if (cultivars.cultivarsItems) {
      cultivarsItems = [...cultivars.cultivarsItems, cultivarsItem]
    } else {
      cultivarsItems = [cultivarsItem]
    }
    setCultivars({ ...cultivars, cultivarsItems })
  }

  function removeCultivarsItem(ids = []) {
    const cultivarsItems = cultivars.cultivarsItems.filter((item) => !ids.includes(item.id))
    setCultivars({ ...cultivars, cultivarsItems })
  }

  const onUpdateCultivarsItem = useCallback(
    (field, cultivarsId, value) => {
      const cultivarsItems = cultivars.cultivarsItems.map((item) => {
        if (cultivarsId && item.id === cultivarsId) {
          return { ...item, [field]: value }
        }
        return item
      })
      setCultivars({ ...cultivars, cultivarsItems })
    },
    [materials, cultivars.cultivarsItems]
  )

  function buildRequestPayload() {
    let cultivarsFormated = {
      id: cultivars.id,
      customerId: +customerId,
      districtId: selectedDistrict,
      cultivarsDate: cultivars.cultivarsDate
    }
    let cultivarsItems = []
    if (cultivars.cultivarsItems) {
      cultivarsItems = cultivars.cultivarsItems.map(sanitizeCultivarItem)
    }
    return { ...cultivarsFormated, cultivarsItems }
  }

  const shouldUpdateCultivars = ({ id, cultivarsDate }) => {
    return id && checkIsSameMonthYear(cultivarsDate)
  }

  function createCultivars(payload) {
    return request(api.createCultivars(payload))
  }

  function updateCultivars({ id, ...payload }) {
    return request(api.updateCultivars(id, payload))
  }

  async function onSubmit() {
    const payload = buildRequestPayload()

    try {
      if (shouldUpdateCultivars(payload)) {
        await updateCultivars(payload)
      } else {
        delete payload.id
        payload.cultivarsDate = moment().toISOString()
        await createCultivars(payload)
      }

      notification.success({
        description: getMessage('successUpdateCultivars'),
        message: getMessage('sweetAlerts.success')
      })

      await fetchCultivars(customerId, selectedDistrict)
    } catch (e) {
      console.tron.log(e)
    }
  }

  const formattedMaterials = useMemo(
    () => materials.map(({ id, name }) => ({ value: id, description: name })),
    [materials]
  )

  const selectedDistrictDescription = useMemo(
    () => (districts || []).find((district) => district.value === selectedDistrict)?.description,
    [selectedDistrict]
  )

  if (!selectedDistrict && districts.length > 0) {
    if (districts.length > 1) {
      return (
        <Select
          style={{ width: 300, margin: '48px 0 48px 50%', transform: 'translate(-50%)' }}
          placeholder="Selecione um distrito"
          onSelect={(districtId) => {
            setSelectedDistrict(districtId)
            fetchCultivars(customerId, districtId)
          }}
        >
          {districts.map(({ value, description }) => (
            <Select.Option key={value} value={value}>
              <p style={{ margin: '0px', marginLeft: '5px' }}>{description}</p>
            </Select.Option>
          ))}
        </Select>
      )
    } else {
      setSelectedDistrict(districts[0].value)
    }
  }

  return (
    <div className="cultivars">
      <Row type="flex" justify="space-between" align="center" style={{ padding: '2%' }}>
        <Typography.Title level={4} style={{ margin: 0, lineHeight: 2 }}>
          Distrito: {selectedDistrictDescription}
        </Typography.Title>
        <Row type="flex">
          {districts.length > 1 && (
            <Button
              type="secondary"
              className="button-action"
              onClick={() => {
                setSelectedDistrict(null)
                setCultivars(cultivarsInitialState)
              }}
            >
              <Icon type="left" theme="outlined" className="default-back-icon" />
              {getMessage('label.back')}
            </Button>
          )}
          <Button type="primary" className="button-action" onClick={onSubmit}>
            {cultivars.id ? getMessage('update') : getMessage('register')}
          </Button>
        </Row>
      </Row>

      <CreateCultivars
        onCreateCultivar={(value) => pushCultivarsItem(value)}
        formattedMaterials={formattedMaterials}
        formattedDemandTypes={demandTypes}
        formattedCultivarsTypes={cultivarsTypes}
        cultivars={cultivars}
        customerId={customerId}
        selectedDistrictId={selectedDistrict}
      />

      <ListCultivars
        cultivarsItems={cultivars.cultivarsItems}
        onRemoveCultivarItems={removeCultivarsItem}
        formattedMaterials={formattedMaterials}
        formattedDemandTypes={demandTypes}
        formattedCultivarsTypes={cultivarsTypes}
        onUpdateCultivarsItem={onUpdateCultivarsItem}
      />

      <SumCultivars organizationId={currentUser.organizationId} cultivars={cultivars} />
    </div>
  )
}

export default Cultivars
